import React, { useState, useEffect } from 'react';
import '../../tailwindcss/Tailwind.css';
import responsibleService from './api.js';
import { useNavigate } from 'react-router-dom';
import GuardrailsDetailModal from './components/GuardrailsDetailModal.js';
import InitiativesTable from './components/InitiativesTable';
import GuardrailsManager from '../guardrails/GuardrailsManager';
import Regression from '../regression/Regression';
import Header from '../../components/Header';
import SummaryMetrics from './components/SummaryMetrics';
import ModelUsageTable from './components/ModalUsageTable.js';
import PromptsTable from './components/PromptsTable';
import PromptDetailsModal from './components/PromptDetailsModal';
import TabSelector from './components/TabSelector.js';
import LlmConfig from './components/LlmConfig.js';

const API_HOST = process.env.REACT_APP_API_URL;

const ResponsibleAI = ({ activeTab, setActiveTab }) => {
  const [promptData, setPromptData] = useState([]);
  const [modelData, setModelData] = useState({});
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dateRange, setDateRange] = useState('7d');
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
  const [customEndDate, setCustomEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchField, setSearchField] = useState('all');
  const [isSearching, setIsSearching] = useState(false);
  const [shouldRefetch, setShouldRefetch] = useState(false);
  const [selectedGuardrail, setSelectedGuardrail] = useState(null);
  const [summaryMetrics, setSummaryMetrics] = useState({
    totalPrompts: 0,
    totalCost: 0,
    avgLatency: 0,
    avgTokens: 0
  });

  const navigate = useNavigate();

  // Generate date filter object based on selected range
  const generateDateFilter = () => {
    if (dateRange === 'custom') {
      return {
        type: 'custom',
        startDate: customStartDate,
        endDate: customEndDate
      };
    } else {
      return {
        type: dateRange
      };
    }
  };

  // Clear search function
  const clearSearch = () => {
    setSearchTerm('');
    setSearchField('all');
    setIsSearching(false);
    setCurrentPage(1);
    setShouldRefetch(true);
  };

  // Handle search
  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      fetchSummaryData();
      return;
    }

    setIsSearching(true);
    setIsLoading(true);
    setCurrentPage(1);

    try {
      const dateFilter = generateDateFilter();

      const countResponse = await responsibleService.getPromptsCount(dateFilter, searchTerm, searchField);
      const totalCount = countResponse.count || 0;

      setTotalRecords(totalCount);
      setTotalPages(Math.ceil(totalCount / pageSize));

      const promptsResponse = await responsibleService.getPaginatedPrompts(dateFilter, 1, pageSize, searchTerm, searchField);
      setPromptData(promptsResponse.result);

      if (promptsResponse.pagination) {
        setTotalPages(promptsResponse.pagination.totalPages || 1);
        setTotalRecords(promptsResponse.pagination.totalRecords || 0);
      }
    } catch (err) {
      console.error("Error searching prompts:", err);
    } finally {
      setIsLoading(false);
      setIsSearching(false);
    }
  };

  // Fetch summary data
  const fetchSummaryData = async () => {
    setIsLoading(true);

    try {
      const dateFilter = generateDateFilter();

      const countResponse = await responsibleService.getPromptsCount(dateFilter);
      const totalCount = countResponse.count || 0;

      if (totalCount === 0) {
        setSummaryMetrics({
          totalPrompts: 0,
          totalCost: 0,
          avgLatency: 0,
          avgTokens: 0
        });
        setModelData({});
        setTotalRecords(0);
        setTotalPages(1);
        setPromptData([]);
        setIsLoading(false);
        return;
      }

      const metricsResponse = await responsibleService.getSummaryMetrics(dateFilter);
      setSummaryMetrics(metricsResponse);

      const modelResponse = await responsibleService.getModelUsage(dateFilter);
      setModelData(modelResponse);

      setTotalRecords(totalCount);
      setTotalPages(Math.ceil(totalCount / pageSize));

      fetchPaginatedPrompts(dateFilter);
    } catch (err) {
      console.error("Error fetching summary data:", err);
      setSummaryMetrics({
        totalPrompts: 0,
        totalCost: 0,
        avgLatency: 0,
        avgTokens: 0
      });
      setModelData({});
      setPromptData([]);
      setTotalRecords(0);
      setTotalPages(1);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch paginated prompts
  const fetchPaginatedPrompts = async (dateFilter) => {
    try {
      const response = await responsibleService.getPaginatedPrompts(
        dateFilter,
        currentPage,
        pageSize,
        searchTerm || '',
        searchField
      );

      if (response && response.result) {
        setPromptData(response.result);

        if (response.pagination) {
          setTotalPages(response.pagination.totalPages || 1);
          setTotalRecords(response.pagination.totalRecords || 0);
        }
      } else {
        console.error("Invalid response format for paginated data:", response);
        setPromptData([]);
      }
    } catch (err) {
      console.error("Error fetching paginated prompts:", err);
      setPromptData([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle refetch when needed
  useEffect(() => {
    if (shouldRefetch) {
      setIsLoading(true);
      fetchSummaryData();
      setShouldRefetch(false);
    }
  }, [shouldRefetch]);

  // Initial data load and when date range changes
  useEffect(() => {
    if (dateRange !== 'custom') {
      fetchSummaryData();
    }
  }, [dateRange]);

  // When custom dates change
  useEffect(() => {
    if (dateRange === 'custom') {
      const timer = setTimeout(() => {
        fetchSummaryData();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [customStartDate, customEndDate, dateRange]);

  // Refetch paginated data when page or size changes
  useEffect(() => {
    if (totalRecords > 0) {
      const dateFilter = generateDateFilter();
      fetchPaginatedPrompts(dateFilter);
    }
  }, [currentPage, pageSize]);

  // Handle prompt selection
  const handlePromptSelect = (prompt) => {
    setSelectedPrompt(prompt);
  };

  // Close prompt details modal
  const closePromptDetails = () => {
    setSelectedPrompt(null);
  };
  
  return (
    <div className="flex min-h-full bg-gray-50">
      <main className="w-full flex-1 p-4 sm:p-6">
        <div className="w-full mt-6">
          

          {activeTab === 'groups' ? (
            <>
              <SummaryMetrics
                summaryMetrics={summaryMetrics}
                dateRange={dateRange}
                setDateRange={setDateRange}
                showCustomDatePicker={showCustomDatePicker}
                setShowCustomDatePicker={setShowCustomDatePicker}
                customStartDate={customStartDate}
                setCustomStartDate={setCustomStartDate}
                customEndDate={customEndDate}
                setCustomEndDate={setCustomEndDate}
                isLoading={isLoading}
                fetchSummaryData={fetchSummaryData}
              />

              <ModelUsageTable
                modelData={modelData}
                isLoading={isLoading}
              />

              <PromptsTable
                promptData={promptData}
                isLoading={isLoading}
                pageSize={pageSize}
                setPageSize={setPageSize}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                totalPages={totalPages}
                totalRecords={totalRecords}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                searchField={searchField}
                setSearchField={setSearchField}
                isSearching={isSearching}
                handleSearch={handleSearch}
                clearSearch={clearSearch}
                handlePromptSelect={handlePromptSelect}
                setSelectedGuardrail={setSelectedGuardrail}
              />
            </>
          ) : activeTab === 'modelEvaluation' ? (
            <Regression />
          ) : activeTab === 'guardrails' ? (
            <GuardrailsManager />
          ) : activeTab === 'llmConfig' ? (
            <LlmConfig />
          ) : (
            <InitiativesTable apiHost={API_HOST} />
          )}
        </div>
      </main>

      {selectedPrompt && (
        <PromptDetailsModal
          selectedPrompt={selectedPrompt}
          closePromptDetails={closePromptDetails}
        />
      )}

      {selectedGuardrail && (
        <GuardrailsDetailModal
          selectedUseCaseId={selectedGuardrail}
          closeModal={() => setSelectedGuardrail(null)}
        />
      )}
    </div>
  );
};

export default ResponsibleAI;